/* eslint-disable @typescript-eslint/no-magic-numbers */

import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

import useApiErrorHandling from '@/hooks/util/useApiErrorHandler';
import { GenericApiResponse } from '@/services/api';
import { isApiResponseSuccess } from '@/utils/helper';

function apiErrorHandlingRequired<T>(
  data: GenericApiResponse<T>,
  option?: boolean | ((data: GenericApiResponse<T>) => boolean),
): boolean {
  const error = data.status < 200 || data.status > 299;
  if (!error) {
    return false;
  }
  if (typeof option === 'boolean') return option;
  if (typeof option === 'function') return option(data);
  return true;
}

function networkErrorHandlingRequired<TError>(
  error: TError,
  option?: boolean | ((error: TError) => boolean),
): boolean {
  if (typeof option === 'boolean') return option;
  if (typeof option === 'function') return option(error);
  return true;
}

type UseApiQueryOptions<
  T = unknown,
  TError = unknown,
  TData = T,
  TQueryKey extends QueryKey = QueryKey,
> = Omit<UseQueryOptions<GenericApiResponse<T>, TError, TData, TQueryKey>, 'select'> & {
  select?: (data: GenericApiResponse<T>) => TData;
  apiErrorHandling?: boolean | ((data: GenericApiResponse<T>) => boolean);
  networkErrorHandling?: boolean | ((error: TError) => boolean);
};

type UseApiMutationOptions<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
> = Omit<
  UseMutationOptions<GenericApiResponse<TData>, TError, TVariables, TContext>,
  'onSuccess'
> & {
  onSuccess?: (
    data: GenericApiResponse<TData>,
    variables: TVariables,
    context: TContext | undefined,
  ) => void;
  apiErrorHandling?: boolean | ((data: GenericApiResponse<TData>) => boolean);
  networkErrorHandling?: boolean | ((error: TError) => boolean);
  dontShowNetworkError?: boolean;
};

function useApiQuery<
  T = unknown,
  TError = unknown,
  TData = T,
  TQueryKey extends QueryKey = QueryKey,
>(options: UseApiQueryOptions<T, TError, TData, TQueryKey>) {
  const { showApiError } = useApiErrorHandling();
  const result = useQuery<GenericApiResponse<T>, TError, TData, TQueryKey>({
    ...options,
    select: (data) => {
      if (!isApiResponseSuccess(data) && apiErrorHandlingRequired(data, options.apiErrorHandling)) {
        showApiError(data);
      }
      return options.select ? options.select(data) : (data.data as TData);
    },
  });

  if (result.isError && networkErrorHandlingRequired(result.error, options.networkErrorHandling)) {
    showApiError(result.error as Error);
  }

  return result;
}

function useApiMutation<TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
  options: UseApiMutationOptions<TData, TError, TVariables, TContext>,
) {
  const { showApiError } = useApiErrorHandling();

  return useMutation<GenericApiResponse<TData>, TError, TVariables, TContext>({
    ...options,
    onSuccess: (data, variables, context) => {
      if (
        !options.dontShowNetworkError &&
        !isApiResponseSuccess(data) &&
        apiErrorHandlingRequired(data, options.apiErrorHandling)
      ) {
        console.log('showApiError1', data, options.dontShowNetworkError);
        showApiError(data);
      }
      options.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      if (
        networkErrorHandlingRequired(
          error,
          options.networkErrorHandling && !options.dontShowNetworkError,
        )
      ) {
        console.log('showApiError2', error);
        showApiError(error as Error);
      }
      options.onError?.(error, variables, context);
    },
    ...(options.mutationKey && {
      scope: {
        id: options.mutationKey[0] as string,
      },
    }),
  });
}

export { useApiMutation, useApiQuery };
